import { put, select, take } from 'redux-saga/effects';
import {
  nextState as requestNextState,
  requestAltchaVerification,
  resetAltchaState,
  setAwaitingAltchaVerification,
  setCaptchaConfiguration,
  setPendingAltchaStateChangeParams,
  setSecurityContextStatus,
} from '../actions';
import {
  LOAD_NODE_SUCCESS,
  SET_ALTCHA_STATUS,
  SET_ALTCHA_TOKEN,
  SET_CAPTCHA_CONFIGURATION,
  SET_SECURITY_CONTEXT_STATUS,
} from '../constants';
import {
  ALTCHA_STATE_VERIFYING,
  ALTCHA_STATE_ERROR,
  CAPTCHA_PROVIDER_ALTCHA,
  CAPTCHA_SECURITY_CONTEXT_STATUS_LOADING,
  CAPTCHA_SECURITY_CONTEXT_STATUS_READY,
  isAltchaProvider,
  isCaptchaRequired,
} from '../utils/captcha';

export function* waitForCaptchaContextReady() {
  while (true) {
    const {
      node: { id, requireFeedbackCaptcha, captchaProvider, securityContextStatus },
    } = yield select();

    if (!id) {
      yield take(LOAD_NODE_SUCCESS);
      continue;
    }

    if (!isCaptchaRequired(requireFeedbackCaptcha)) {
      return;
    }

    if (securityContextStatus === CAPTCHA_SECURITY_CONTEXT_STATUS_LOADING || !captchaProvider) {
      yield take([LOAD_NODE_SUCCESS, SET_CAPTCHA_CONFIGURATION, SET_SECURITY_CONTEXT_STATUS]);
      continue;
    }

    return;
  }
}

export function* activateAltchaProvider() {
  yield put(
    setCaptchaConfiguration({
      captchaProvider: CAPTCHA_PROVIDER_ALTCHA,
    }),
  );
  yield put(setSecurityContextStatus(CAPTCHA_SECURITY_CONTEXT_STATUS_READY));
  yield put(resetAltchaState());
}

export function* waitForAltchaToken() {
  const {
    flow: { altchaToken },
  } = yield select();

  if (altchaToken) {
    return true;
  }

  yield put(requestAltchaVerification());

  while (true) {
    const action = yield take([SET_ALTCHA_TOKEN, SET_ALTCHA_STATUS]);

    if (action.type === SET_ALTCHA_TOKEN && action.payload) {
      return true;
    }

    if (action.type === SET_ALTCHA_STATUS && action.payload?.status === ALTCHA_STATE_ERROR) {
      return false;
    }
  }
}

export function* handleCaptchaBeforeFeedbackCreation(state, stateChangeParams) {
  if (!isCaptchaRequired(state.node.requireFeedbackCaptcha)) {
    return false;
  }

  // An offline submission is persisted without waiting for captcha.
  if (window.navigator.onLine === false) {
    return false;
  }

  if (state.node.securityContextStatus === CAPTCHA_SECURITY_CONTEXT_STATUS_LOADING || !state.node.captchaProvider) {
    return true;
  }

  if (isAltchaProvider(state.node.captchaProvider) && !state.flow.altchaToken) {
    yield put(setPendingAltchaStateChangeParams(stateChangeParams));

    if (state.flow.altchaStatus !== ALTCHA_STATE_VERIFYING) {
      yield put(requestAltchaVerification());
    }

    yield put(setAwaitingAltchaVerification(true));
    return true;
  }

  return false;
}

export function* resumePendingAltchaStateChangeSaga({ payload }) {
  if (!payload) {
    return;
  }

  const state = yield select();
  const pendingStateChangeParams = state.flow.pendingAltchaStateChangeParams;

  if (!state.flow.isAwaitingAltchaVerification || !pendingStateChangeParams) {
    return;
  }

  yield put(requestNextState(pendingStateChangeParams));
}
